import React, { useState, useEffect } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useAuthContext } from "../contexts/AuthContext";
import AdminQBank from "./AdminQBank";
import AdminInstagram from "./AdminInstagram";
import AdminEmail from "./AdminEmail";
import AdminTelegram from "./AdminTelegram";
import AdminSupervisors from "./AdminSupervisors";
import AdminGroupInvoices from "./AdminGroupInvoices";
import AdminManualInvoices from "./AdminManualInvoices";
import AdminCoupons from "./AdminCoupons";
import AdminAnnouncements from "./AdminAnnouncements";
import AdminChatFeedback from "./AdminChatFeedback";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import styles from "./Admin.module.css";

const AdminAnalytics = React.lazy(() => import("./AdminAnalytics"));
const AdminIOSUpdates = React.lazy(() => import("./AdminIOSUpdates"));

// SVG Icons for sidebar
const AnalyticsIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 3v18h18" />
        <path d="m7 16 4-5 4 3 5-7" />
    </svg>
);

const QBankIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        <path d="M8 7h8" />
        <path d="M8 11h8" />
        <path d="M8 15h4" />
    </svg>
);
const InstagramIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
);

const TelegramIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 2L11 13" />
        <path d="M22 2L15 22L11 13L2 9L22 2Z" />
    </svg>
);

const EmailIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
    </svg>
);

const SupervisorIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);

const InvoiceIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16l4-2 4 2 4-2 4 2V8z" />
        <path d="M14 2v6h6" />
        <path d="M8 13h8" />
        <path d="M8 17h5" />
    </svg>
);

const ManualInvoiceIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15V6a2 2 0 0 0-2-2H8l-5 5v10a2 2 0 0 0 2 2h8" />
        <path d="M14 22v-6a2 2 0 0 1 2-2h6" />
        <path d="M3 9h5V4" />
        <path d="M17 17h.01" />
        <path d="M20 17h.01" />
        <path d="M17 20h.01" />
        <path d="M20 20h.01" />
    </svg>
);

const CouponIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3H4a1 1 0 0 0-1 1v5.59A2 2 0 0 0 3.59 11l9.59 9.59a2 2 0 0 0 2.82 0L20.59 16a2 2 0 0 0 0-2.59z" />
        <path d="M7 7h.01" />
    </svg>
);

const AnnouncementIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m3 11 18-5v12L3 14v-3z" />
        <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
);

const ChatFeedbackIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z" />
        <path d="M8 9h8" />
        <path d="M8 13h5" />
    </svg>
);

// Main tabs: rendered once visited, kept mounted, toggled via display
const TAB_PATHS = [
    "/admin/analytics",
    "/admin/chat-feedback",
    "/admin/qbank",
    "/admin/instagram",
    "/admin/telegram",
    "/admin/email",
    "/admin/coupons",
    "/admin/announcements",
    "/admin/ios-updates",
    "/admin/supervisors",
    "/admin/manual-invoices",
    "/admin/group-invoices",
];

const Admin: React.FC = () => {
    const { user, loading: authLoading } = useAuthContext();
    const location = useLocation();
    const navigate = useNavigate();
    const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
    const [checkingAdmin, setCheckingAdmin] = useState(false);
    const [mountedTabs, setMountedTabs] = useState<Set<string>>(new Set());

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

    const navItems = [
        { label: "Analytics", path: "/admin/analytics", icon: <AnalyticsIcon /> },
        { label: "Chat Feedback", path: "/admin/chat-feedback", icon: <ChatFeedbackIcon /> },
        { label: "QBank", path: "/admin/qbank", icon: <QBankIcon /> },
        { label: "Instagram", path: "/admin/instagram", icon: <InstagramIcon /> },
        { label: "Telegram", path: "/admin/telegram", icon: <TelegramIcon /> },
        { label: "Email", path: "/admin/email", icon: <EmailIcon /> },
        { label: "Coupons", path: "/admin/coupons", icon: <CouponIcon /> },
        { label: "Announcements", path: "/admin/announcements", icon: <AnnouncementIcon /> },
        { label: "iOS Updates", path: "/admin/ios-updates", icon: <AnnouncementIcon /> },
        { label: "Supervisors", path: "/admin/supervisors", icon: <SupervisorIcon /> },
        { label: "Manual Invoices", path: "/admin/manual-invoices", icon: <ManualInvoiceIcon /> },
        { label: "Group Invoices", path: "/admin/group-invoices", icon: <InvoiceIcon /> },
    ];

    const isActive = (path: string) => location.pathname.startsWith(path);

    const currentPath = location.pathname;
    const isMainTab = TAB_PATHS.includes(currentPath);
    const isDetailPage = !isMainTab && currentPath !== "/admin";

    // Lazy-mount: only render a tab component once it has been visited
    useEffect(() => {
        if (isMainTab) {
            setMountedTabs(prev => {
                if (prev.has(currentPath)) return prev;
                return new Set(prev).add(currentPath);
            });
        }
    }, [currentPath, isMainTab]);

    useEffect(() => {
        // Wait for auth to finish loading before checking admin status
        if (authLoading) {
            return;
        }

        // Already confirmed admin — don't re-check on every navigation
        if (isAdmin) {
            return;
        }

        const checkAdminStatus = async () => {
            if (!user?.id) {
                // Not logged in, redirect to login
                navigate("/login", { state: { from: location.pathname } });
                return;
            }

            setCheckingAdmin(true);
            try {
                const res = await authenticatedFetch(`${apiUrl}/api/auth/is-admin/${user.id}`);
                if (res.ok) {
                    const data = await res.json();
                    if (!data.is_admin) {
                        // Not an admin, redirect to home
                        navigate("/");
                        return;
                    }
                    setIsAdmin(true);
                } else {
                    navigate("/");
                }
            } catch (err) {
                console.error("Failed to check admin status", err);
                navigate("/");
            } finally {
                setCheckingAdmin(false);
            }
        };

        checkAdminStatus();
    }, [user, authLoading, navigate, location.pathname, apiUrl, isAdmin]);

    // Show loading while auth is loading or checking admin status
    if (authLoading || checkingAdmin || isAdmin === null) {
        return (
            <div className={styles.pageContainer}>
                <SEO title="Admin | C-Shaped" />
                <Header />
                <main className={styles.mainContent}>
                    <div className={styles.loadingContainer}>
                        <div className={styles.loadingSpinner} />
                        <p>Verifying access...</p>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    // If not admin, this won't render (redirect happens above)
    if (!isAdmin) {
        return null;
    }

    return (
        <div className={styles.pageContainer}>
            <SEO title="Admin | C-Shaped" />
            <Header />

            <main className={styles.mainContent}>
                <div className={styles.adminLayout}>
                    {/* Sidebar */}
                    <aside className={styles.sidebar}>
                        <h2 className={styles.sidebarTitle}>Admin</h2>
                        <nav className={styles.sidebarNav}>
                            {navItems.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`${styles.navItem} ${isActive(item.path) ? styles.navItemActive : ""
                                        }`}
                                >
                                    <span className={styles.navIcon}>{item.icon}</span>
                                    {item.label}
                                </Link>
                            ))}
                        </nav>
                    </aside>

                    {/* Content */}
                    <div className={styles.content}>
                        {currentPath === "/admin" && <Navigate to="/admin/analytics" replace />}

                        {/* Main tabs — kept mounted once visited, toggled via display */}
                        {mountedTabs.has("/admin/analytics") && (
                            <div style={{ display: currentPath === "/admin/analytics" ? undefined : "none" }}>
                                <React.Suspense fallback={(
                                    <div className={styles.loadingContainer}>
                                        <div className={styles.loadingSpinner} />
                                        <p>Loading analytics...</p>
                                    </div>
                                )}>
                                    <AdminAnalytics />
                                </React.Suspense>
                            </div>
                        )}
                        {mountedTabs.has("/admin/chat-feedback") && (
                            <div style={{ display: currentPath === "/admin/chat-feedback" ? undefined : "none" }}>
                                <AdminChatFeedback />
                            </div>
                        )}
                        {mountedTabs.has("/admin/qbank") && (
                            <div style={{ display: currentPath === "/admin/qbank" ? undefined : "none" }}>
                                <AdminQBank />
                            </div>
                        )}
                        {mountedTabs.has("/admin/instagram") && (
                            <div style={{ display: currentPath === "/admin/instagram" ? undefined : "none" }}>
                                <AdminInstagram />
                            </div>
                        )}
                        {mountedTabs.has("/admin/telegram") && (
                            <div style={{ display: currentPath === "/admin/telegram" ? undefined : "none" }}>
                                <AdminTelegram />
                            </div>
                        )}
                        {mountedTabs.has("/admin/email") && (
                            <div style={{ display: currentPath === "/admin/email" ? undefined : "none" }}>
                                <AdminEmail />
                            </div>
                        )}
                        {mountedTabs.has("/admin/coupons") && (
                            <div style={{ display: currentPath === "/admin/coupons" ? undefined : "none" }}>
                                <AdminCoupons />
                            </div>
                        )}
                        {mountedTabs.has("/admin/announcements") && (
                            <div style={{ display: currentPath === "/admin/announcements" ? undefined : "none" }}>
                                <AdminAnnouncements />
                            </div>
                        )}
                        {mountedTabs.has("/admin/ios-updates") && (
                            <div style={{ display: currentPath === "/admin/ios-updates" ? undefined : "none" }}>
                                <React.Suspense fallback={<p>Loading iOS updates…</p>}>
                                    <AdminIOSUpdates />
                                </React.Suspense>
                            </div>
                        )}
                        {mountedTabs.has("/admin/supervisors") && (
                            <div style={{ display: currentPath === "/admin/supervisors" ? undefined : "none" }}>
                                <AdminSupervisors />
                            </div>
                        )}
                        {mountedTabs.has("/admin/manual-invoices") && (
                            <div style={{ display: currentPath === "/admin/manual-invoices" ? undefined : "none" }}>
                                <AdminManualInvoices />
                            </div>
                        )}
                        {mountedTabs.has("/admin/group-invoices") && (
                            <div style={{ display: currentPath === "/admin/group-invoices" ? undefined : "none" }}>
                                <AdminGroupInvoices />
                            </div>
                        )}

                        {/* Detail pages — normal mount/unmount via Outlet */}
                        {isDetailPage && <Outlet />}
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default Admin;
